import { CHIP_PACKS, ECONOMY, type ChipPack } from '@tira/protocol';
import { useState, type JSX } from 'react';
import { Button } from '../components/Button.js';
import { ChipStack } from '../components/ChipStack.js';
import { Icon } from '../components/Icon.js';
import { useT } from '../i18n/index.js';
import { api } from '../net/api.js';
import { useApp } from '../state/store.js';
import { chips } from './lib/format.js';
import { Disclosure, Screen } from './parts/Screen.js';
import './Store.css';

/**
 * S13 — the chip store. Four packs, the middle one framed best-value (§10), and
 * the no-cash-value line sitting with the goods rather than in the small print.
 * Responsible play is one tap from here because §13 requires it from every
 * store surface — it is in the rail and again in the card below the packs.
 */
export function Store(): JSX.Element {
  const go = useApp((s) => s.go);
  const setToast = useApp((s) => s.setToast);
  const refreshMe = useApp((s) => s.refreshMe);
  const t = useT();
  const [busy, setBusy] = useState<string | null>(null);
  const [capped, setCapped] = useState(false);

  const buy = (pack: ChipPack) => {
    setBusy(pack.productId);
    setCapped(false);
    api
      .purchase(pack.productId)
      .then((res) => {
        setBusy(null);
        if (res.blockedByCap) {
          setCapped(true);
          return;
        }
        setToast(t('account.store.bought', { n: chips(res.credited) }), 'win');
        void refreshMe();
      })
      .catch((e: Error) => {
        setBusy(null);
        setToast(t('account.store.fail', { err: e.message }), 'warn');
      });
  };

  return (
    <Screen title={t('account.store.title')} sub={t('account.store.sub')}>
      <section className="s-panel store__rack">
        <ChipStack count={5} />
        <div className="s-row__main">
          <b>{t('account.store.rack')}</b>
          <span>{t('account.store.rack.note', { n: chips(ECONOMY.starterRack) })}</span>
        </div>
        <span className="store__sandbox">{t('account.store.sandbox.flag')}</span>
      </section>

      <section className="s-panel">
        <div className="s-panel__head">
          <h2>{t('account.store.packs')}</h2>
          <p>{t('account.store.packs.sub')}</p>
        </div>

        <Disclosure text={t('account.disclosure')} />

        <div className="store__packs">
          {CHIP_PACKS.map((pack, i) => (
            <article className="pack" key={pack.productId} data-best={pack.bestValue}>
              {pack.bestValue ? <span className="pack__flag">{t('account.store.best')}</span> : null}
              <div className="pack__stack">
                <ChipStack count={i + 2} />
              </div>
              <p className="pack__chips">
                {chips(pack.chips)}
                <small>{t('account.chips')}</small>
              </p>
              <div className="pack__price">
                <b>{t('account.store.price', { php: pack.pricePhp })}</b>
                <span>{t('account.store.price.note')}</span>
              </div>
              <Button variant={pack.bestValue ? 'primary' : 'secondary'} disabled={busy !== null} onClick={() => buy(pack)}>
                {busy === pack.productId ? t('account.store.busy') : t('account.store.buy')}
              </Button>
            </article>
          ))}
        </div>

        <p className="s-note">{t('account.store.sandbox.note')}</p>

        {capped ? (
          <div className="s-empty">
            <b>{t('account.store.capped')}</b>
            <span>{t('account.store.capped.sub')}</span>
            <Button variant="secondary" onClick={() => go({ s: 'settings' })}>
              {t('account.store.settings')}
            </Button>
          </div>
        ) : null}
      </section>

      <section className="s-panel s-panel--quiet">
        <div className="store__care">
          <Icon name="responsible" size={22} />
          <div className="s-row__main">
            <b>{t('account.care.title')}</b>
            <span>{t('account.store.care.note')}</span>
          </div>
          <Button variant="secondary" onClick={() => go({ s: 'settings' })}>
            {t('account.store.open')}
          </Button>
        </div>
      </section>
    </Screen>
  );
}
